import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Pencil, Trash, Plus, X, Check, CaretDown, CaretUp } from '@phosphor-icons/react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const TAGS = [
  { value: '', label: 'None' },
  { value: 'bestseller', label: 'Bestseller' },
  { value: 'most-ordered', label: 'Most Ordered' },
  { value: 'healthy-choice', label: 'Healthy Choice' },
];

const CATEGORY_ICONS = {
  Sandwiches: '🥪', Breakfast: '🍽️', 'Healthy Meals': '🥗',
  Biryani: '🍚', Parathas: '🫓', Thali: '🍛',
  'Healthy Rolls (No Maida)': '🌯', Burgers: '🍔', Drinks: '🥤',
};

const TAG_STYLES = {
  bestseller:      'bg-amber-100 text-amber-700',
  'most-ordered':  'bg-blue-100 text-blue-700',
  'healthy-choice':'bg-emerald-100 text-emerald-700',
};

const BLANK_ITEM = { name: '', price: '', is_veg: true, tag: '', description: '', is_available: true };

export function AdminCateringMenuTab() {
  const [menu, setMenu] = useState([]);
  const [collapsed, setCollapsed] = useState({});
  const [editingItem, setEditingItem] = useState(null);   // { item_id, ...fields }
  const [editForm, setEditForm] = useState({});
  const [addingTo, setAddingTo] = useState(null);         // category name
  const [addForm, setAddForm] = useState(BLANK_ITEM);
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    try {
      const { data } = await axios.get(`${BACKEND_URL}/api/admin/menu/catering`, { withCredentials: true });
      setMenu(data);
    } catch { toast.error('Failed to load menu'); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const startEdit = (item) => {
    setEditingItem(item.item_id);
    setEditForm({ name: item.name, price: item.price, is_veg: item.is_veg, tag: item.tag || '', description: item.description || '', is_available: item.is_available });
    setAddingTo(null);
  };

  const cancelEdit = () => { setEditingItem(null); setEditForm({}); };

  const saveEdit = async (itemId) => {
    setSaving(true);
    try {
      await axios.put(`${BACKEND_URL}/api/admin/menu/catering/${itemId}`, {
        ...editForm, price: Number(editForm.price), tag: editForm.tag || null,
      }, { withCredentials: true });
      toast.success('Item updated');
      setEditingItem(null);
      load();
    } catch { toast.error('Failed to save'); }
    finally { setSaving(false); }
  };

  const deleteItem = async (itemId, name) => {
    if (!window.confirm(`Delete "${name}"?`)) return;
    try {
      await axios.delete(`${BACKEND_URL}/api/admin/menu/catering/${itemId}`, { withCredentials: true });
      toast.success('Item deleted');
      load();
    } catch { toast.error('Failed to delete'); }
  };

  const saveAdd = async (category, icon) => {
    if (!addForm.name || !addForm.price) { toast.error('Name and price are required'); return; }
    setSaving(true);
    try {
      await axios.post(`${BACKEND_URL}/api/admin/menu/catering`, {
        ...addForm, price: Number(addForm.price), category, category_icon: icon, tag: addForm.tag || null,
      }, { withCredentials: true });
      toast.success('Item added');
      setAddingTo(null);
      setAddForm(BLANK_ITEM);
      load();
    } catch { toast.error('Failed to add item'); }
    finally { setSaving(false); }
  };

  const toggleCollapse = (cat) => setCollapsed(p => ({ ...p, [cat]: !p[cat] }));

  const totalItems = menu.reduce((sum, cat) => sum + cat.items.length, 0);

  return (
    <div className="space-y-4" data-testid="catering-menu-tab">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div>
          <h2 className="text-xl font-semibold text-[#1C1917]">Corporate Menu</h2>
          <p className="text-sm text-stone-500 mt-0.5">{menu.length} categories · {totalItems} items</p>
        </div>
        <button
          onClick={() => { setAddingTo('__new__'); setAddForm(BLANK_ITEM); }}
          className="flex items-center gap-2 px-4 py-2 rounded-xl bg-[#1A4331] text-white text-sm font-medium hover:bg-[#2A5A44] transition-all"
          data-testid="add-category-btn"
        >
          <Plus size={16} /> Add Item
        </button>
      </div>

      {/* Add to new/custom category */}
      {addingTo === '__new__' && (
        <NewCategoryForm
          onSave={async (cat, icon) => { await saveAdd(cat, icon); }}
          onCancel={() => setAddingTo(null)}
          addForm={addForm}
          setAddForm={setAddForm}
          saving={saving}
        />
      )}

      {/* Categories */}
      {menu.map((cat) => (
        <div key={cat.category} className="bg-white border border-stone-200 rounded-2xl overflow-hidden">
          {/* Category header */}
          <div
            className="flex items-center justify-between px-5 py-4 cursor-pointer hover:bg-stone-50 transition-colors"
            onClick={() => toggleCollapse(cat.category)}
          >
            <div className="flex items-center gap-3">
              <span className="text-2xl">{cat.icon}</span>
              <div>
                <h3 className="font-semibold text-[#1C1917]">{cat.category}</h3>
                <p className="text-xs text-stone-400">{cat.items.length} items</p>
              </div>
            </div>
            <div className="flex items-center gap-3">
              <button
                onClick={(e) => { e.stopPropagation(); setAddingTo(cat.category); setAddForm(BLANK_ITEM); setEditingItem(null); }}
                className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-[#F0F9F4] text-[#1A4331] text-xs font-medium hover:bg-[#D1FAE5] transition-all"
                data-testid={`add-item-btn-${cat.category}`}
              >
                <Plus size={13} /> Add item
              </button>
              {collapsed[cat.category] ? <CaretDown size={16} className="text-stone-400" /> : <CaretUp size={16} className="text-stone-400" />}
            </div>
          </div>

          {/* Add item form inside this category */}
          {addingTo === cat.category && (
            <div className="px-5 pb-4 border-t border-stone-100 bg-emerald-50/40">
              <p className="text-xs font-semibold text-[#1A4331] py-3">New item in {cat.category}</p>
              <ItemFormFields form={addForm} setForm={setAddForm} />
              <div className="flex gap-2 mt-3">
                <button onClick={() => saveAdd(cat.category, cat.icon)} disabled={saving}
                  className="px-4 py-2 rounded-xl bg-[#1A4331] text-white text-sm font-medium disabled:opacity-50">
                  {saving ? 'Adding…' : 'Add Item'}
                </button>
                <button onClick={() => setAddingTo(null)} className="px-4 py-2 rounded-xl bg-stone-100 text-stone-700 text-sm">Cancel</button>
              </div>
            </div>
          )}

          {/* Items list */}
          {!collapsed[cat.category] && (
            <div className="divide-y divide-stone-100">
              {cat.items.map((item) => (
                <div key={item.item_id} className="px-5 py-3">
                  {editingItem === item.item_id ? (
                    /* Inline edit form */
                    <div className="bg-stone-50 rounded-xl p-4">
                      <ItemFormFields form={editForm} setForm={setEditForm} />
                      <div className="flex gap-2 mt-3">
                        <button onClick={() => saveEdit(item.item_id)} disabled={saving}
                          className="flex items-center gap-1.5 px-4 py-2 rounded-xl bg-[#1A4331] text-white text-sm font-medium disabled:opacity-50">
                          <Check size={14} /> {saving ? 'Saving…' : 'Save'}
                        </button>
                        <button onClick={cancelEdit} className="flex items-center gap-1.5 px-4 py-2 rounded-xl bg-stone-100 text-stone-700 text-sm">
                          <X size={14} /> Cancel
                        </button>
                      </div>
                    </div>
                  ) : (
                    /* Item row */
                    <div className="flex items-center gap-3">
                      {/* Veg/Non-Veg dot */}
                      <span className={`w-3.5 h-3.5 rounded-sm border-2 flex-shrink-0 flex items-center justify-center ${item.is_veg ? 'border-green-600' : 'border-red-600'}`}>
                        <span className={`w-1.5 h-1.5 rounded-full ${item.is_veg ? 'bg-green-600' : 'bg-red-600'}`} />
                      </span>

                      {/* Name + tag */}
                      <div className="flex-1 min-w-0">
                        <span className={`text-sm font-medium text-[#1C1917] ${!item.is_available ? 'line-through opacity-50' : ''}`}>
                          {item.name}
                        </span>
                        {item.tag && (
                          <span className={`ml-2 text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 rounded-full ${TAG_STYLES[item.tag] || 'bg-stone-100 text-stone-600'}`}>
                            {item.tag}
                          </span>
                        )}
                        {item.description && <p className="text-xs text-stone-400 mt-0.5 truncate">{item.description}</p>}
                      </div>

                      {/* Price */}
                      <span className="text-sm font-bold text-[#1A4331] w-16 text-right">₹{item.price}</span>

                      {/* Actions */}
                      <div className="flex items-center gap-1 ml-2">
                        <button onClick={() => startEdit(item)}
                          className="p-1.5 rounded-lg hover:bg-stone-100 text-stone-500 hover:text-[#1A4331] transition-colors"
                          data-testid={`edit-item-${item.item_id}`} title="Edit">
                          <Pencil size={15} />
                        </button>
                        <button onClick={() => deleteItem(item.item_id, item.name)}
                          className="p-1.5 rounded-lg hover:bg-red-50 text-stone-400 hover:text-red-500 transition-colors"
                          data-testid={`delete-item-${item.item_id}`} title="Delete">
                          <Trash size={15} />
                        </button>
                      </div>
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>
      ))}
    </div>
  );
}

function ItemFormFields({ form, setForm }) {
  const f = (k, v) => setForm(p => ({ ...p, [k]: v }));
  return (
    <div className="grid grid-cols-2 gap-3">
      <div className="col-span-2 sm:col-span-1">
        <label className="text-xs font-medium text-stone-500 block mb-1">Item Name *</label>
        <input value={form.name} onChange={e => f('name', e.target.value)}
          className="w-full border border-stone-200 rounded-xl px-3 py-2 text-sm outline-none focus:border-[#1A4331]"
          placeholder="e.g. Paneer Sandwich" />
      </div>
      <div>
        <label className="text-xs font-medium text-stone-500 block mb-1">Price (₹) *</label>
        <input type="number" value={form.price} onChange={e => f('price', e.target.value)}
          className="w-full border border-stone-200 rounded-xl px-3 py-2 text-sm outline-none focus:border-[#1A4331]"
          placeholder="120" />
      </div>
      <div>
        <label className="text-xs font-medium text-stone-500 block mb-1">Tag</label>
        <select value={form.tag || ''} onChange={e => f('tag', e.target.value)}
          className="w-full border border-stone-200 rounded-xl px-3 py-2 text-sm outline-none focus:border-[#1A4331] bg-white">
          {TAGS.map(t => <option key={t.value} value={t.value}>{t.label}</option>)}
        </select>
      </div>
      <div>
        <label className="text-xs font-medium text-stone-500 block mb-1">Type</label>
        <div className="flex gap-3 mt-2">
          <label className="flex items-center gap-1.5 text-sm cursor-pointer">
            <input type="radio" checked={form.is_veg === true} onChange={() => f('is_veg', true)} className="accent-green-600" /> Veg
          </label>
          <label className="flex items-center gap-1.5 text-sm cursor-pointer">
            <input type="radio" checked={form.is_veg === false} onChange={() => f('is_veg', false)} className="accent-red-500" /> Non-Veg
          </label>
        </div>
      </div>
      <div className="col-span-2">
        <label className="text-xs font-medium text-stone-500 block mb-1">Description (optional)</label>
        <input value={form.description || ''} onChange={e => f('description', e.target.value)}
          className="w-full border border-stone-200 rounded-xl px-3 py-2 text-sm outline-none focus:border-[#1A4331]"
          placeholder="Short description" />
      </div>
      <div className="col-span-2 flex items-center gap-2">
        <input type="checkbox" id="avail" checked={form.is_available} onChange={e => f('is_available', e.target.checked)}
          className="accent-[#1A4331] w-4 h-4" />
        <label htmlFor="avail" className="text-sm text-stone-600 cursor-pointer">Available on menu</label>
      </div>
    </div>
  );
}

function NewCategoryForm({ onSave, onCancel, addForm, setAddForm, saving }) {
  const [catName, setCatName] = useState('');
  const [catIcon, setCatIcon] = useState('');
  return (
    <div className="bg-white border border-[#1A4331]/20 rounded-2xl p-5">
      <h3 className="font-semibold text-[#1A4331] mb-4 text-sm">Add Item (New Category)</h3>
      <div className="grid grid-cols-2 gap-3 mb-3">
        <div>
          <label className="text-xs font-medium text-stone-500 block mb-1">Category Name *</label>
          <input value={catName} onChange={e => setCatName(e.target.value)}
            className="w-full border border-stone-200 rounded-xl px-3 py-2 text-sm outline-none focus:border-[#1A4331]"
            placeholder="e.g. Snacks" />
        </div>
        <div>
          <label className="text-xs font-medium text-stone-500 block mb-1">Category Icon (emoji)</label>
          <input value={catIcon} onChange={e => setCatIcon(e.target.value)}
            className="w-full border border-stone-200 rounded-xl px-3 py-2 text-sm outline-none focus:border-[#1A4331]"
            placeholder="🍿" />
        </div>
      </div>
      <ItemFormFields form={addForm} setForm={setAddForm} />
      <div className="flex gap-2 mt-4">
        <button onClick={() => onSave(catName, catIcon)} disabled={saving || !catName}
          className="px-4 py-2 rounded-xl bg-[#1A4331] text-white text-sm font-medium disabled:opacity-50">
          {saving ? 'Adding…' : 'Add Item'}
        </button>
        <button onClick={onCancel} className="px-4 py-2 rounded-xl bg-stone-100 text-stone-700 text-sm">Cancel</button>
      </div>
    </div>
  );
}

export default AdminCateringMenuTab;
